import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { ElasticFormModal, InfiniteScroll, ListRow, NoResults, Search, WithSelectable, WithTooltip } from 'basic-components';
import { Exit } from 'basic-components/icons';
import { DataStorageEnums, SymbolSearchQuery } from 'dashboard-services/api/dataStorage';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';
import { Fetching } from 'primary-components';

import getApiConfig from 'api/ApiConfig';
import { dataStorageQueries } from 'queries/modules/datastorage';
import { sendEvent } from 'services/GAService';

import SymbolFilter from './SymbolFilter';

import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { When } from 'react-if';

import FilterCarousel from 'primary/FilterCarousel';
import '../../Modal.scss';
import './Modal.scss';

const isSymbolSelected = (value, symbol) => {
  let selected = value?.groupName === symbol?.groupName
  selected && Object.entries(value?.symbols || {}).forEach(([k, v]) => {
    if(v !== symbol.symbols[k]) {
      selected = false;
    }
  })
  return selected;
}
const Modal = ({ closeModal, optionalText = undefined, onChange, defaultValue = "" }) => {
  const dispatch = useDispatch(),
        [value, setValue] = useState(defaultValue),
        [tags, setTags] = useState([]),
        [query, setQuery] = useState(""),
        [groupName, setGroupName] = useState(undefined),
        activeFilterCount = Number(Boolean(groupName)) + tags.length,
        clearAllFilters = useCallback(() => {
          setGroupName(undefined)
          setTags([])
        }, []),
        formProps = useMemo(() => ({
          noDebounce: true
        }), []),
        getUrl = useCallback(item => DashboardLinkFactory.dataStorage.newDataPrep({ groupName: item?.groupName, query: "", storageScreen: "Table", symbol: Object.entries(item?.symbols || {}).map(([k, v]) => `${k}=${v}`).join("|") }), []),
        queryCalculated = useMemo(() => {
          const symbols = tags.filter(rs => rs.type === DataStorageEnums.KEY_TYPE.SYMBOL),
                metadatas = tags.filter(rs => rs.type === DataStorageEnums.KEY_TYPE.METADATA)
          return new SymbolSearchQuery.Builder()
            .withGroupName(groupName)
            .withSymbols(symbols)
            .withMetadatas(metadatas)
            .withQueryString(query ? `${query}*` : "")
            .build()
            .getQuery()
        }, [groupName, query, tags]),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() =>
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
        , [getApiConfigLocal]),
        { data: symbolsRaw, hasNextPage, fetchNextPage: onScroll, isPending, isFetchingNextPage } = dataStorageQueries.useSearchSymbols({
          getQueryBuilder,
          query: queryCalculated,
          size: 20,
          shouldRefetch: false,
        }),
        symbols = useMemo(() => (symbolsRaw?.pages || []).flatMap(p => p.items || []), [symbolsRaw]),
        onSubmit = useCallback(() => onChange({ symbols: value.symbols, groupName: value.groupName }), [onChange, value]),
        submitProps = useMemo(() => ({
          disabled: value === undefined || value === "",
          noDebounce: true,
          type: "button",
          onClick: () => {
            onSubmit()
            closeModal()
          }
        }), [value, onSubmit, closeModal]),
        preventFilterSubmit = useCallback(event => event.preventDefault(), []),
        scrollContRef = useRef()

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "symbol_picker",
      label: "picker",
    };
    dispatch(sendEvent(event))
  }, [dispatch])

  return (
    <ElasticFormModal
        className="ng-common-picker__modal ng-symbol-picker__modal"
        formProps={formProps}
        onClose={closeModal}
        onSubmit={onSubmit}
        optionalText={optionalText}
        submitProps={submitProps}
        submitText="Select"
        title="Select symbol"
    >
      <div className="ng-file-picker__modal__top">
        <div className="ng-file-picker__modal__top__search">
          <Search
              data-testid="symbol-search"
              onSearch={setQuery}
              size="large"
              variant="outline"
          />
        </div>
        <FilterCarousel
            activeFilterCount={activeFilterCount}
            className="ng-file-picker__modal__filters"
            onClearAll={clearAllFilters}
            onClickCapture={preventFilterSubmit}
        >
          <SymbolFilter
              groupName={groupName}
              query={query}
              setGroupName={setGroupName}
              setTags={setTags}
              tags={tags}
          />
        </FilterCarousel>
      </div>
      <div className="ng-common-picker__modal__headers">
        <div className="ng-common-picker__modal__headers__cell">
          Group
        </div>
        <div className="ng-common-picker__modal__headers__cell">
          Symbols
        </div>
        <div className="ng-common-picker__modal__headers__cell"/>
      </div>
      <Fetching
          isFetching={isPending || isFetchingNextPage}
      />
      <div 
          className="ng-common-picker__modal__content"
          ref={scrollContRef}
      >
        <InfiniteScroll
            className="ng-common-picker__modal__content__scroll"
            hasMore={hasNextPage}
            initialLoad={false}
            loadMore={onScroll}
            threshold={800}
            useWindow={false}
        >
          {symbols.map((symbol, index) => (
            <WithSelectable
                index={index}
                key={`Pipeline${index}`}
                selected={isSymbolSelected(value, symbol)}
                setSelected={() => setValue(symbol)}
            >
              <ListRow
                  className="ng-common-picker__modal__content__scroll__row"
                  contentProps={{
                    onDoubleClick: () => {
                      setValue(symbol)
                      onSubmit()
                      closeModal()
                    }
                  }}
                  onClick={() => setValue(symbol)}
                  scrollContRef={scrollContRef.current}
              >
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {symbol.groupName}
                </div>
                <div className="ng-common-picker__modal__content__scroll__row__cell ng-common-picker__modal__content__scroll__row__cell--symbols">
                  <div className="ng-common-picker__modal__symbols__main">
                    <table>
                      <thead>
                        <tr>
                          {Object.keys(symbol.symbols || {}).map((key, index) => <th key={"table-header " + index}>{key + ":"}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {Object.values(symbol.symbols || {}).map((value, index) => <td key={"table-cell " + index}>{value}</td>)}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div> 
                <div className="ng-common-picker__modal__exit">
                  <WithTooltip
                      data-place="top"
                      short
                      text="Open pipeline"
                  >
                    <div onClick={() => CommonService.openInNewTab(getUrl(symbol))}>
                      <Exit
                          color="blue-bright"
                          height={16}
                          width={16}
                      />
                    </div>
                  </WithTooltip>
                </div>
              </ListRow>
            </WithSelectable>
          ))}
        </InfiniteScroll>
        <When condition={symbols.length === 0 && !isPending}>
          {() => (
            <NoResults
                isModal
            />
          )}
        </When>
      </div>
    </ElasticFormModal>
  )
}

Modal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  optionalText: PropTypes.string,
}

export default memo(Modal);
